import { CacheStrategyName, queryCacheStrategies } from 'api';
import { UseQueryOptions } from 'react-query';
import { saveCredentials, getCredentialValue } from './localStorageManagement';

export type HttpMethod = 'GET' | 'PUT' | 'POST' | 'DELETE';

export async function getBearerToken() {
  let authBearerToken = getCredentialValue('authBearerToken');

  const tokenExpirationTimestamp = getCredentialValue(
    'authBearerTokenExpiration'
  );
  const tokenExpirationDateTime = tokenExpirationTimestamp
    ? new Date(tokenExpirationTimestamp)
    : null;
  const now = new Date();
  if (tokenExpirationDateTime && now >= tokenExpirationDateTime) {
    const authResponse = await getNewTokenData();
    saveCredentials({
      authBearerToken: authResponse.token,
      authBearerTokenExpiration: authResponse.expirationDate,
      authRefreshToken: authResponse.refreshToken,
    });
    authBearerToken = authResponse.token;
  }
  return authBearerToken;
}

const defaultHeaders = {
  'content-type': 'application/json',
  'x-api-authorization': 'allowed',
  'x-client-app-version': process.env.REACT_APP_VERSION ?? 'undefined',
  'x-client-type': 'WEB',
};

/**
 * @name getNewTokenData
 * @description Super-slim helper to grab current session tokens
 *
 * @returns token object from API
 */
export async function getNewTokenData() {
  const path = '/authenticate';
  const authBearerToken = getCredentialValue('authRefreshToken');
  const host = `${process.env.REACT_APP_RECONVELOCITY_APPSERVICE_URL_OVERRIDE}`;
  const options = {
    method: 'GET',
    headers: {
      ...defaultHeaders,
      Authorization: `Bearer ${authBearerToken}`,
    },
  };
  const data = await fetch(host + path, options).then((res) => res.json());
  return data;
}

/**
 * @name getAPIHeaders
 * @description  function to generate boilerplate for API calls.
 * */

export async function getAPIHeaders(
  method: HttpMethod = 'GET',
  headers = {}
): Promise<{ host: string; options: any }> {
  const host = `${process.env.REACT_APP_RECONVELOCITY_APPSERVICE_URL_OVERRIDE}`;
  const tenantId = window.location.pathname.split('/')[1];
  const authBearerToken = await getBearerToken();

  const options = {
    method,
    headers: {
      ...defaultHeaders,
      ...headers,
      'auth-tenant-id': `${tenantId}`,
    } as Record<string, string>,
  };
  if (authBearerToken) {
    options.headers.Authorization = `Bearer ${authBearerToken}`;
  }
  return { host, options };
}

export function makeQueryString(obj: { [key: string]: any }) {
  return Object.getOwnPropertyNames(obj)
    .map((key) => {
      const val = obj[key];
      return val !== null ? `${key}::${obj[key]}` : null;
    })
    .join(',');
}

export function makeOptionsFromSettings<T>(
  settings?: UseQueryOptions<T, Error> | CacheStrategyName
) {
  if (!settings) {
    return queryCacheStrategies.basic;
  }

  return typeof settings === 'string'
    ? queryCacheStrategies[settings as CacheStrategyName]
    : settings;
}
